import { AsyncPipe } from "@angular/common";
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { FamilyStep } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { AutocompleteSearchFilterPipe } from "@odx/angular/components/autocomplete";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { isSameCentralStationOption } from "@project/monitoring/central-station-configuration/central-station-configuration.utils";
import { isGroupedOption } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringOptionCardComponent } from "@project/monitoring/monitoring-option-card/monitoring-option-card.component";
import { OptionDisplay, OptionDisplayService } from "@project/monitoring/option-display.service";
import { BehaviorSubject, combineLatest, forkJoin, of, ReplaySubject, Subject } from "rxjs";
import { map, shareReplay, switchMap } from "rxjs/operators";

@Component({
  selector: "app-central-station-licence-selector",
  imports: [
    AsyncPipe,
    FilterProductComponent,
    FormsModule,
    ReactiveFormsModule,
    MonitoringOptionCardComponent,
    UiKitModule,
    AutocompleteSearchFilterPipe,
  ],
  templateUrl: "./central-station-licence-selector.component.html",
  styleUrl: "./central-station-licence-selector.component.scss",
})
export class CentralStationLicenceSelectorComponent implements OnInit {
  private monitoringProductService = inject(MonitoringProductService);
  private formBuilder = inject(FormBuilder);
  private optionDisplayService = inject(OptionDisplayService);

  @Input({ required: true }) title!: string;
  @Input({ required: true }) category!: MonitoringCategory;
  @Input() categoryLabel?: string;
  @Input() familyName?: string;
  @Input() showCategoryHeaders = true;

  @Input() set step(step: FamilyStep | undefined) {
    if (!step) return;
    this._step = step;
    this.step$.next(step);
  }

  @Input() set originalOption(option: MonitoringProductItem | undefined) {
    if (option) {
      this.optionDisplayService
        .createOptionDisplay(option.option, this.category)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((optionDisplay) => {
          this.pendingOriginalOptionDisplay = optionDisplay;
          this.tryApplyOriginalSelection(this.pendingOriginalOptionDisplay, this.latestOptionDisplays);
        });
    }
  }

  protected licenceForm = this.formBuilder.group({
    selectedLicence: new FormControl<null | OptionDisplay>(null, Validators.required),
    filterPatients: new FormControl<string[] | null>(null),
    filterRetention: new FormControl<string[] | null>(null),
  });

  @Output() licenceSelect = new EventEmitter<OptionDisplay>();
  @Output() formReady = new EventEmitter<FormGroup>();

  protected selectedOption?: FamilyStepOption;
  protected readonly optionDisplays$ = new ReplaySubject<OptionDisplay[]>(1);
  protected readonly patientFilterOptions$ = new ReplaySubject<string[]>(1);
  protected readonly retentionFilterOptions$ = new ReplaySubject<string[]>(1);

  private readonly step$ = new Subject<FamilyStep>();

  private latestOptionDisplays: OptionDisplay[] | null = null;
  private pendingOriginalOptionDisplay: OptionDisplay | null = null;

  private _step?: FamilyStep;
  private readonly searchValue$ = new BehaviorSubject<string | null>(null);
  private readonly filterPatientsValue$ = new BehaviorSubject<string[] | null>(null);
  private readonly filterRetentionValue$ = new BehaviorSubject<string[] | null>(null);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly filteredOptionDisplays$ = combineLatest([
    this.searchValue$,
    this.filterPatientsValue$,
    this.filterRetentionValue$,
    this.optionDisplays$,
  ]).pipe(
    map(([searchValue, filterPatients, filterRetention, optionDisplays]) => {
      let filteredOptions = optionDisplays as OptionDisplay[];
      if (filterPatients && filterPatients.length > 0) {
        filteredOptions = filteredOptions.filter((option) => filterPatients.includes(option.option.filter1 ?? ""));
      }
      if (filterRetention && filterRetention.length > 0) {
        filteredOptions = filteredOptions.filter((option) => filterRetention.includes(option.option.filter2 ?? ""));
      }
      if (searchValue) {
        filteredOptions = filteredOptions.filter((option) =>
          this.monitoringProductService.searchFilterOption(option, searchValue),
        );
      }
      return filteredOptions;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly groupedOptionDisplays$ = this.filteredOptionDisplays$.pipe(
    map((options) => options.filter((option) => isGroupedOption(option.option))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly singleOptionDisplays$ = this.filteredOptionDisplays$.pipe(
    map((options) => options.filter((option) => !isGroupedOption(option.option))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly sortedOptionDisplays$ = this.filteredOptionDisplays$.pipe(
    map((options) => this.sortOptionDisplays(options)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    this.step$
      .pipe(
        switchMap((step) => {
          this.selectedOption = undefined;

          this.licenceForm.patchValue(
            { selectedLicence: null, filterPatients: null, filterRetention: null },
            { emitEvent: false },
          );
          this.licenceForm.markAsPristine();
          this.licenceForm.markAsUntouched();

          this.searchValue$.next(null);
          this.filterPatientsValue$.next(null);
          this.filterRetentionValue$.next(null);

          const options = step.options ?? [];
          if (!options.length) {
            this.latestOptionDisplays = [];
            this.pendingOriginalOptionDisplay = null;
            this.optionDisplays$.next([]);
            this.initFilters([]);
            return of(null);
          }

          const optionDisplaysObservables = options.map((option) =>
            this.optionDisplayService.createOptionDisplay(option, MonitoringCategory.CENTRAL_STATIONS),
          );

          return forkJoin(optionDisplaysObservables).pipe(map((results) => ({ results, options })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((payload) => {
        if (!payload) return;
        this.latestOptionDisplays = payload.results;
        this.optionDisplays$.next(payload.results);
        this.initFilters(payload.options);

        this.tryApplyOriginalSelection(this.pendingOriginalOptionDisplay, payload.results);
      });

    const selectedControl = this.licenceForm.get("selectedLicence");
    const filterPatientsControl = this.licenceForm.get("filterPatients");
    const filterRetentionControl = this.licenceForm.get("filterRetention");

    selectedControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selected) => {
      if (selected) {
        this.selectedOption = selected.option;
        this.licenceSelect.emit(selected);
      }
    });

    filterPatientsControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      this.filterPatientsValue$.next(val);
    });

    filterRetentionControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      this.filterRetentionValue$.next(val);
    });
  }

  ngOnInit(): void {
    this.formReady.emit(this.licenceForm);
  }

  get step(): FamilyStep | undefined {
    return this._step;
  }

  protected select(option: OptionDisplay) {
    this.licenceForm.patchValue({ selectedLicence: option });
  }

  protected search(value: string) {
    this.searchValue$.next(value);
  }

  protected isOptionSelected(option: OptionDisplay): boolean {
    if (!this.selectedOption) return false;
    return isSameCentralStationOption(this.familyName, option.option, this.selectedOption);
  }

  private sortOptionDisplays(options: OptionDisplay[]): OptionDisplay[] {
    return [...options].sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }));
  }

  private initFilters(options: FamilyStepOption[]) {
    const patientValues = this.uniqueSortedValues(options.map((option) => option.filter1));
    const retentionValues = this.uniqueSortedValues(options.map((option) => option.filter2));
    this.patientFilterOptions$.next(patientValues);
    this.retentionFilterOptions$.next(retentionValues);
  }

  private uniqueSortedValues(values: Array<string | null | undefined>) {
    const filtered = (values ?? []).filter((value): value is string => value !== undefined && value !== null && value !== "");
    const unique = Array.from(new Set(filtered));
    return unique.sort((a, b) => {
      const numA = Number(a);
      const numB = Number(b);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        return numA - numB;
      }
      return a.localeCompare(b);
    });
  }

  private applyOriginalSelection(optionDisplay: OptionDisplay) {
    this.licenceForm.patchValue({ selectedLicence: optionDisplay }, { emitEvent: false });
    this.selectedOption = optionDisplay.option;
    this.licenceSelect.emit(optionDisplay);
  }

  private tryApplyOriginalSelection(pending: OptionDisplay | null, options: OptionDisplay[] | null): void {
    if (!pending || !options?.length) return;

    const match = options.find((display) => isSameCentralStationOption(this.familyName, display.option, pending.option));

    if (!match) return;

    this.applyOriginalSelection(match);
    this.pendingOriginalOptionDisplay = null;
  }
}
